import site from '../../site';
import siteLocales from '../../site-locales';
import globals from '../../globals';
import history from '../../history';
import customization from '../../customization';
import initialData from '../utils/initial-data';
import actualizeInitialData from '../utils/actualize-intial-data';
import setStatus from '../actions/set-status';
import getStatus from '../selectors/status';
import statuses from '../constants/statuses';
import setSiteMeta from '../utils/base-saving-actions/set-site-meta';
import save from '../thunks/save';

const savingManager = (store) => (next) => (action) => {
  switch (action.type) {
    case globals.actionTypes.SET_GLOBALS: {
      next(action);
      initialData.setGlobals(action.payload.globals);

      break;
    }

    case site.actionTypes.SET_PAGES: {
      next(action);
      initialData.setPages(action.payload.pages);
      break;
    }

    case site.actionTypes.RELOAD_SITE_META:
    case site.actionTypes.CONNECT_SITE: {
      next(action);
      actualizeInitialData(store.getState());
      const { properties } = action.payload.site;
      const siteProperties = {
        ...properties,
        siteProgressChecklist:
          properties.siteProgressChecklist || site.constants.DEFAULT_SITE_PROGRESS_CHECKLIST,
      };
      initialData.setPages(action.payload.site.meta.pages);
      initialData.setFolders(action.payload.site.meta.folders);
      initialData.setSettings(action.payload.site.meta.settings);
      initialData.setActivePageId(action.payload.site.meta.activePage);
      initialData.setProperties(siteProperties);
      initialData.setSiteName(action.payload.site.name);
      initialData.setRedirects(action.payload.site.meta.redirects);
      initialData.setIntegrations(action.payload.site.meta.integrations);

      if (action.payload.site.meta.onlinestore) {
        initialData.setCleanUrlsEnabled(action.payload.site.meta.onlinestore.cleanUrlsEnabled);
      }

      if (action.payload.site.meta.customization) {
        initialData.setCustomTextColors(action.payload.site.meta.customization.customTextColors);
        initialData.setCustomColors(action.payload.site.meta.customization.customColors);
        initialData.setCustomFontPairs(action.payload.site.meta.customization.customFontPairs);
        initialData.setCustomGradients(action.payload.site.meta.customization.customGradients);
      }

      break;
    }

    case site.actionTypes.SET_PAGE_HTML: {
      const state = store.getState();
      const page = site.selectors.getPage(state, action.payload.pageId);
      const hasHTML = Boolean(page.html);
      const savingStatus = getStatus(state);

      if (
        (savingStatus === statuses.SAVED || savingStatus === statuses.ERROR) &&
        !history.verifiers.isHistoryEmpty()
      ) {
        store.dispatch(setStatus(statuses.UNSAVED));
      }
      next(action);

      if (!hasHTML) {
        const updatedState = store.getState();
        const updatedPage = site.selectors.getPage(updatedState, action.payload.pageId);
        initialData.setPageHtml(updatedPage.id, updatedPage.html);
      }
      break;
    }

    case globals.actionTypes.SET_GLOBAL_HTML_AND_AFFECT_HISTORY:
    case globals.actionTypes.SET_GLOBAL_HTML: {
      const state = store.getState();
      const global = globals.selectors.getGlobal(state, action.payload.globalId);
      const hasHTML = Boolean(global.html);
      const initialGlobalHTML = initialData.getGlobalHtml(action.payload.globalId);

      /* For some cases (for example, inject online store id to the initialization script) we have to update content of some globals before it will go to storage.
      It works as following:
      We intercept `SET_GLOBAL_HTML` and look into content, if it's necessary we change global content and
      dispatch SET_GLOBAL_HTML action with new content (without calling `next`, in order to prevent storing non updated content to the store),
      if not - just call `next`.
      We set global's html into initialData cache based on action's payload data only once,
      and while making changes of global's content we update only it's html set in the store.
      Thus, we are able to create new saving action of global's content update based on comparison of data set in store and initial data.
      */
      if (!hasHTML && !initialGlobalHTML) {
        initialData.setGlobalHtml(action.payload.globalId, action.payload.html);
      } else if (initialGlobalHTML !== action.payload.html) {
        store.dispatch(setStatus(statuses.UNSAVED));
      }

      next(action);
      break;
    }

    case site.actionTypes.ADD_BINDING:
    case site.actionTypes.UPDATE_BINDING:
    case site.actionTypes.REMOVE_BINDING:
    case site.actionTypes.SET_SITE_PROPERTY:
    case site.actionTypes.UPDATE_PAGE_SETTINGS:
    case site.actionTypes.UPDATE_SETTINGS:
    case site.actionTypes.UPDATE_REDIRECTS:
    case site.actionTypes.SET_SITE_NAME:
    case site.actionTypes.UPDATE_INTEGRATIONS:
    case site.actionTypes.ADD_FOLDER:
    case site.actionTypes.DELETE_FOLDER:
    case site.actionTypes.RENAME_FOLDER:
    case site.actionTypes.MOVE_TO_FOLDER:
    case customization.actionTypes.DELETE_CUSTOM_FONT_PAIR: {
      const state = store.getState();
      const savingStatus = getStatus(state);

      if (savingStatus === statuses.SAVED || savingStatus === statuses.ERROR) {
        store.dispatch(setStatus(statuses.UNSAVED));
      }

      next(action);
      break;
    }

    case site.actionTypes.SET_ACTIVE_PAGE: {
      const state = store.getState();
      const savingStatus = getStatus(state);
      const activePageId = site.selectors.getActivePageId(state);

      if (
        (savingStatus === statuses.SAVED || savingStatus === statuses.ERROR) &&
        activePageId &&
        activePageId !== action.payload.pageId
      ) {
        store.dispatch(setStatus(statuses.UNSAVED));
      }

      next(action);
      break;
    }

    case siteLocales.actionTypes.SAVE_MAIN_LOCALE: {
      const updateAction = setSiteMeta(['mainLocale'], action.payload.locale);
      const actions = [updateAction];

      store.dispatch(save(actions)).then((response) => {
        action.payload.resolve(response.data);
        next(action);
      });

      break;
    }

    default: {
      next(action);
    }
  }
};

export default savingManager;
